import { FeaturedDiscounts } from "@/components/hub/FeaturedDiscounts";
import type { FeaturedDeal } from "../../../../lib/ordering-visuals/featuredDiscounts";
import { useQuery, useQueries } from "@tanstack/react-query";
import { normaliseDeliveryPostcode, requestDeliveryFee, withDeliveryFee } from "../../../../lib/ordering-visuals/deliveryFees";
/**
 * Food discovery / Shop Local storefront (web).
 *
 * PRESENTATION-ONLY redesign, brought into line with the mobile app
 * (mobile/app/(tabs)/order.tsx). The data contract is untouched:
 *   • useGetOrderStores(params) — town, lat/lon, search, type, postcode+fulfilment
 *   • useGetOrderCategories()
 * Category, "open now" and "offers" narrowing happen client-side over the fully
 * loaded result set (the query sends no limit/offset, so nothing is hidden by
 * pagination). No pricing, fee or availability logic lives in this file.
 *
 * Layout notes — the four lessons from the app redesign, applied to browser:
 *  1. Chrome before the feed is kept to two compact rows plus a category rail.
 *     Restaurants are visible without scrolling on every breakpoint.
 *  2. Pills are used for the segmented control, category rail and filter chips
 *     only. Metadata is plain text; the primary CTA is the card itself.
 *  3. Cards keep real photography and a card silhouette — this is a food page.
 *  4. Desktop width buys MORE CARDS PER ROW, not larger ones. Card metrics are
 *     identical at 768 and 1920; only the column count changes.
 */
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import categoryPhotos from "../../../../lib/ordering-visuals/categories.json";
import { useState, useMemo, useEffect } from "react";
import { Link } from "wouter";
import { useGetOrderStores, useGetOrderCategories } from "@workspace/api-client-react";
import { useSEO } from "@/hooks/use-seo";
import { useTown } from "@/hooks/use-town";
import FeedAdSlot from "@/components/FeedAdSlot";
import { useAuth } from "@/hooks/use-auth";
import { usePendingOrders } from "@/context/PendingOrdersContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QueryState } from "@/components/QueryState";
import {
  BROWSE_GRID, CategoryRail, ELEVATION, FilterChip, FOCUS_RING, RADII, ScrollRow,
  Segmented, StoreBrowseCard, StoreBrowseCardSkeleton, StoreBrowseRow,
  StoreBrowseRowSkeleton, StoreCard, TYPE,
  haversineMi, isSponsored, storeIsOpen, type BrowseStore,
} from "@/components/hub";
import { cn } from "@/lib/utils";
import {
  Search, Star, ChevronLeft, MapPin, ChevronDown, Loader2, Navigation,
  LayoutList, LayoutGrid, Truck, Store, X, Tag, Clock, ShoppingBag,
} from "lucide-react";

type Fulfilment = "delivery" | "collection";
const PREFS_KEY = "user_ordering_prefs_v1";

function loadPrefs(): { postcode: string | null; fulfilment: Fulfilment } {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) { const saved = JSON.parse(raw); return { postcode: null, fulfilment: saved?.fulfilment === "collection" ? "collection" : "delivery" }; }
  } catch { /* ignore */ }
  return { postcode: null, fulfilment: "delivery" };
}
function savePrefs(p: { postcode: string | null; fulfilment: Fulfilment }) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

type StoreType = "food" | "retail";
type ViewMode  = "list" | "grid";

const VIEW_MODE_KEY = "order_stores_view";
/** Written by OrderStoreDetail on every store view. Read-only here. */
const RECENT_KEY = "ah_recently_viewed_stores";

/** Sits directly below the sticky 64px site navbar (Navbar.tsx: `h-16`). */
const STICKY_TOP = "top-16";

const STOREFRONT_CONFIG = {
  food: {
    title: "Order Food",
    eyebrow: "Local restaurants & takeaways",
    description: "Order from local Ayrshire restaurants and takeaways.",
    emoji: "",
    searchPlaceholder: "Search restaurants or dishes...",
    emptyText: "No food or takeaway stores in this area yet",
    otherHref: "/shop-local",
    otherTitle: "Looking for local shops?",
    otherText: "Browse Shop Local",
  },
  retail: {
    title: "Shop Local",
    eyebrow: "Retail, groceries & local services",
    description: "Shop with independent Ayrshire retailers, grocers and local services.",
    emoji: "",
    searchPlaceholder: "Search local shops...",
    emptyText: "No local shops or services in this area yet",
    otherHref: "/order",
    otherTitle: "Hungry? Order food",
    otherText: "Browse restaurants & takeaways",
  },
} satisfies Record<StoreType, Record<string, string>>;


/**
 * Ordering, matched to the app (mobile/app/(tabs)/order.tsx:865):
 * open first, then sponsored, then nearest. Array.prototype.sort is stable, so
 * the API's own ordering survives inside each group.
 */
function sortStores(stores: BrowseStore[]): BrowseStore[] {
  return [...stores].sort((a, b) => {
    const aOpen = storeIsOpen(a), bOpen = storeIsOpen(b);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    const aSp = isSponsored(a), bSp = isSponsored(b);
    if (aSp !== bSp) return aSp ? -1 : 1;
    const aD = a.distMi ?? null, bD = b.distMi ?? null;
    if (aD === null && bD === null) return 0;
    if (aD === null) return 1;
    if (bD === null) return -1;
    return aD - bD;
  });
}

// ─── Location ─────────────────────────────────────────────────────────────────

/**
 * Detect the shopper's town once, on first visit.
 *
 * This used to live inside a `<LocationPicker />` pill in the filter row, but
 * that pill duplicated the town control already in the page header — the same
 * choice offered twice, two rows apart. The pill is gone; the detection it was
 * quietly responsible for is not, so it lives here instead.
 */
// ─── Main page ────────────────────────────────────────────────────────────────

function StorefrontStores({ storeType }: { storeType: StoreType }) {
  const storefront = STOREFRONT_CONFIG[storeType];
  useSEO({ title: storefront.title, description: storefront.description });
  const { selectedTown, townGroups, deliveryPostcode, locationLabel, location: sharedLocation, openLocationPicker } = useTown();
  const { user } = useAuth();
  const { hasStore } = usePendingOrders();
  const [subcategory, setSubcategory] = useState(() =>
    storeType === "retail" && new URLSearchParams(window.location.search).get("category") === "Groceries"
      ? "Groceries" : "All"
  );
  const [showCategories, setShowCategories] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [openNowOnly, setOpenNowOnly] = useState(false);
  const [offersOnly, setOffersOnly] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try { return (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) ?? "grid"; } catch { return "grid"; }
  });
  const [localPrefs, setPrefsState] = useState<{ postcode: string | null; fulfilment: Fulfilment }>(loadPrefs);
  const prefs = { ...localPrefs, postcode: deliveryPostcode };
  const [recentIds, setRecentIds] = useState<number[]>([]);

  function setPrefs(update: Partial<{ postcode: string | null; fulfilment: Fulfilment }>) {
    setPrefsState((prev) => {
      const next = { ...prev, ...update, postcode: deliveryPostcode };
      savePrefs(next);
      return next;
    });
  }

  // Read-only: the same list OrderStoreDetail writes on every store view.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Array<{ id: number }>;
      setRecentIds(parsed.map((r) => r.id));
    } catch { /* ignore */ }
  }, []);

  function setView(mode: ViewMode) {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* ignore */ }
  }

  function clearFilters() {
    setSubcategory("All");
    setSearch("");
    setOpenNowOnly(false);
    setOffersOnly(false);
  }

  const selectedTownEntry = useMemo(
    () => townGroups.flatMap(g => g.towns).find(t => t.name === selectedTown),
    [selectedTown, townGroups]
  );
  const userLocation = sharedLocation.mode === "postcode" && sharedLocation.lat != null && sharedLocation.lon != null ? { lat: sharedLocation.lat, lng: sharedLocation.lon } : selectedTownEntry ? { lat: selectedTownEntry.lat, lng: selectedTownEntry.lon } : null;
  const townQueryParam = selectedTown !== "All Towns" ? selectedTown : undefined;

  const { data, isLoading, isError, refetch } = useGetOrderStores({
    town: townQueryParam,
    lat: selectedTownEntry?.lat,
    lon: selectedTownEntry?.lon,
    search: search || undefined,
    type: storeType,
    ...(prefs.fulfilment === "delivery" && prefs.postcode ? { postcode: prefs.postcode, fulfilment: "delivery" } : {}),
  });
  const { data: catData } = useGetOrderCategories();

  const quotePostcode = normaliseDeliveryPostcode(prefs.postcode);
  const quoteStores = (data?.items ?? []).filter(s => s.offersDelivery && s.hasDeliveryZones);
  const quoteResults = useQueries({ queries: quoteStores.map(store => ({ queryKey: ["store-delivery-fee", store.id, quotePostcode], enabled: !!quotePostcode, staleTime: 60000, retry: false, queryFn: ({ signal }: { signal: AbortSignal }) => requestDeliveryFee(store.id, quotePostcode, signal) })) });
  const quotesById = new Map(quoteStores.map((s, i) => [s.id, quoteResults[i]]));
  const { data: dealsData } = useQuery<{ deals: FeaturedDeal[] }>({ queryKey: ["homepage-deals"], enabled: storeType === "food", staleTime: 60000, queryFn: async () => { const r = await fetch("/api/order/homepage-deals"); if (!r.ok) throw new Error("Offers unavailable"); return r.json(); } });
  const allStores = (data?.items ?? []).map(store => { const quote = quotesById.get(store.id); return withDeliveryFee(store, quotePostcode, quote?.data, quote?.isError); });
  const allCats = catData?.items ?? [];
  const foodCats = useMemo(() => allCats.filter((c) => c.type === "food").map((c) => c.name), [allCats]);
  const retailCats = useMemo(() => allCats.filter((c) => c.type === "retail").map((c) => c.name), [allCats]);
  const subCats = useMemo(() => (storeType === "food" ? foodCats : retailCats), [storeType, foodCats, retailCats]);

  /** Category-narrowed and sorted. Quick filters apply on top of this. */
  const sortedStores = useMemo(() => {
    const filtered = (allStores as BrowseStore[])
      .filter((s) => {
        const cats = s.categories?.length ? s.categories : [s.category];
        return subcategory === "All" || cats.includes(subcategory);
      })
      .map((s) => ({
        ...s,
        distMi:
          userLocation && s.storeLatitude != null && s.storeLongitude != null
            ? haversineMi(userLocation.lat, userLocation.lng, s.storeLatitude, s.storeLongitude)
            : null,
      }));
    return sortStores(filtered);
  }, [allStores, subcategory, userLocation]);

  const stores = useMemo(() => sortedStores.filter((s) => {
    if (openNowOnly && !storeIsOpen(s)) return false;
    if (offersOnly && (s.activeOffers?.length ?? 0) === 0) return false;
    return true;
  }), [sortedStores, openNowOnly, offersOnly]);

  const openStores   = useMemo(() => stores.filter(storeIsOpen), [stores]);
  const closedStores = useMemo(() => stores.filter((s) => !storeIsOpen(s)), [stores]);

  const openCount   = sortedStores.filter(storeIsOpen).length;
  const offersCount = sortedStores.filter((s) => (s.activeOffers?.length ?? 0) > 0).length;

  const hasActiveFilter = subcategory !== "All" || search.trim().length > 0 || openNowOnly || offersOnly;
  const townLabel = selectedTown === "All Towns" ? "Ayrshire" : selectedTown;

  const recentStores = useMemo(() => {
    if (hasActiveFilter || recentIds.length === 0) return [];
    const byId = new Map(sortedStores.map((s) => [s.id, s]));
    return recentIds.map((id) => byId.get(id)).filter(Boolean).slice(0, 8) as BrowseStore[];
  }, [recentIds, sortedStores, hasActiveFilter]);

  const categoryImages = storeType === "food"
    ? Object.fromEntries(Object.entries(categoryPhotos).map(([category, photo]) => [category, `/images/food-categories/${photo}.jpg`]))
    : Object.fromEntries(allStores.filter((store) => store.bannerUrl || store.imageUrl).flatMap((store) =>
                (store.categories?.length ? store.categories : [store.category]).filter(Boolean).map((category) =>
                  [category, store.bannerUrl || store.imageUrl || ""])));

  const isGrid = viewMode === "grid";

  /* Skeletons mirror the real card metrics exactly, so the feed does not
     jump when data lands. Count roughly fills the tallest supported grid. */
  const skeleton = isGrid ? (
    <div className={BROWSE_GRID}>
      {Array.from({ length: 8 }).map((_, i) => <StoreBrowseCardSkeleton key={i} />)}
    </div>
  ) : (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 6 }).map((_, i) => <StoreBrowseRowSkeleton key={i} />)}
    </div>
  );

  // Weave an advert into the feed after roughly every this-many stores.
  const AD_EVERY = 8;
  const adTown = selectedTown !== "All Towns" ? selectedTown : undefined;

  const renderStores = (list: BrowseStore[], weave = false) => {
    if (!weave) {
      return isGrid ? (
        <div className={BROWSE_GRID}>{list.map((s) => <StoreBrowseCard key={s.id} store={s} />)}</div>
      ) : (
        <div className="flex flex-col gap-3">{list.map((s) => <StoreBrowseRow key={s.id} store={s} />)}</div>
      );
    }
    // Weave full-width ad slots between chunks of stores so ads appear throughout the feed.
    const chunks: BrowseStore[][] = [];
    for (let i = 0; i < list.length; i += AD_EVERY) chunks.push(list.slice(i, i + AD_EVERY));
    return (
      <div className="flex flex-col gap-4">
        {chunks.map((chunk, ci) => (
          <div key={ci} className="flex flex-col gap-4">
            {isGrid ? (
              <div className={BROWSE_GRID}>{chunk.map((s) => <StoreBrowseCard key={s.id} store={s} />)}</div>
            ) : (
              <div className="flex flex-col gap-3">{chunk.map((s) => <StoreBrowseRow key={s.id} store={s} />)}</div>
            )}
            {(ci < chunks.length - 1 || chunks.length === 1) && <FeedAdSlot placement="food" town={adTown} index={ci} forceFormat="compact" />}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ── Controls ───────────────────────────────────────────────────
          Two compact rows plus the category rail. Deliberately not a hero:
          the first restaurant should be on screen immediately. */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-5 pt-4 pb-4">
          {/* The site navbar already provides Back below lg — don't repeat it. */}
          <button
            type="button"
            onClick={() => window.history.back()}
            className={cn(TYPE.footnote, FOCUS_RING, RADII.xs, "hidden lg:flex items-center gap-1 text-slate-500 hover:text-[#1e3a5f] transition-colors -ml-1 mb-2 cursor-pointer")}
          >
            <ChevronLeft size={15} /> Back
          </button>

          {/* Row 1 — identity and, on desktop, the search field beside it so
              the two do not cost two full rows. */}
          <div className="flex items-center justify-between gap-4 flex-wrap md:flex-nowrap">
            <div className="min-w-0 flex-1 md:flex-none">
              <button type="button" aria-label="Change location" onClick={() => openLocationPicker()} className={cn(FOCUS_RING, "text-left flex items-center gap-2 min-h-11 text-[#1e3a5f]")}>
                <MapPin size={20} className="shrink-0" />
                <span><span className="block text-[10px] font-semibold uppercase tracking-[1.4px] text-slate-500">{prefs.fulfilment === "delivery" ? "Deliver to" : "Find food near"}</span><span className="block text-lg font-bold tracking-tight">{prefs.fulfilment === "delivery" && prefs.postcode ? prefs.postcode : locationLabel}</span></span>
                <ChevronLeft size={16} className="-rotate-90" />
              </button>
              <h1 className="sr-only">{storefront.title}</h1>
            </div>

            <div className="relative order-3 md:order-2 w-full md:w-auto md:flex-1 md:max-w-xl">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                type="search"
                placeholder={storefront.searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={storefront.searchPlaceholder}
                className="pl-10 h-12 rounded-full bg-slate-100 border-0 text-[15px]"
              />
            </div>

            <div className="flex items-center gap-2 order-2 md:order-3 shrink-0">
              <Link href={storefront.otherHref} className={cn(TYPE.caption, RADII.pill, FOCUS_RING, "hidden lg:inline-flex items-center gap-1.5 h-8 px-3 border border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900 transition-colors")}>
                <Store size={14} aria-hidden />
                {storefront.otherText}
              </Link>

            </div>
          </div>

          <p className="mt-2 text-xs text-slate-600">A separate platform service fee, normally £1.50 per order, applies. Delivery and any shop charges are shown before you pay.</p>

          {/* Row 2 — fulfilment, location, postcode, quick filters, result
              count and the view toggle. One row on desktop; wraps on phone. */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {/* On a phone these scroll sideways as ONE row rather than
                wrapping to three — three wrapped rows of controls was the
                single biggest thing pushing restaurants below the fold.
                From `sm` up there is room to wrap and they sit on one line. */}
            <div className="contents sm:flex sm:items-center sm:gap-2 sm:flex-1 sm:min-w-0 sm:py-0.5 sm:flex-wrap">
              <Segmented
                ariaLabel="Delivery or collection"
                className="shrink-0"
                value={prefs.fulfilment}
                onChange={(v) => setPrefs({ fulfilment: v })}
                options={[
                  { value: "delivery", label: "Delivery", icon: <Truck size={12} aria-hidden /> },
                  { value: "collection", label: "Collection", icon: <Store size={12} aria-hidden /> },
                ]}
              />

              <FilterChip
                tone="success"
                icon={<Clock size={12} aria-hidden />}
                selected={openNowOnly}
                onClick={() => setOpenNowOnly((v) => !v)}
              >
                {openCount > 0 ? `Open now (${openCount})` : "Open now"}
              </FilterChip>

              {offersCount > 0 && (
                <FilterChip
                  tone="warning"
                  icon={<Tag size={12} aria-hidden />}
                  selected={offersOnly}
                  onClick={() => setOffersOnly((v) => !v)}
                >
                  Offers ({offersCount})
                </FilterChip>
              )}

              {hasActiveFilter && (
                <FilterChip icon={<X size={12} aria-hidden />} onClick={clearFilters}>Clear</FilterChip>
              )}
            </div>

            <div className="flex items-center gap-3 shrink-0 ml-auto">
              {stores.length > 0 && (
                <span className={cn(TYPE.footnote, "text-slate-500 hidden sm:inline")}>
                  {stores.length} {stores.length === 1 ? "shop" : "shops"} in {townLabel}
                </span>
              )}
              <div className={cn(RADII.sm, "flex items-center border border-slate-200 overflow-hidden bg-white")}>
                {([["grid", LayoutGrid, "Grid view"], ["list", LayoutList, "List view"]] as const).map(([mode, Icon, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setView(mode)}
                    title={label}
                    aria-label={label}
                    aria-pressed={viewMode === mode}
                    className={cn(
                      FOCUS_RING, "grid place-items-center w-9 h-9 sm:w-8 sm:h-8 transition-colors cursor-pointer",
                      viewMode === mode ? "bg-[#1e3a5f] text-white" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    <Icon size={15} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {storeType === "food" && !!dealsData?.deals?.length && <div className="container mx-auto px-4 pb-5"><FeaturedDiscounts deals={dealsData.deals} /></div>}

        {/* Row 3 — category rail. Sticks under the site navbar so the user can
            re-filter from any scroll depth, exactly as the app's rail pins. */}
        <div className={cn("sticky z-30 bg-white/95 backdrop-blur border-t border-slate-100", STICKY_TOP)}>
          <div className="container mx-auto px-4 flex items-center gap-3">
            <div className="min-w-0 flex-1"><CategoryRail
              items={["All", ...subCats]}
              selected={subcategory}
              onSelect={setSubcategory}
              images={categoryImages}
            /></div>
            <button type="button" onClick={() => setShowCategories(true)} aria-label="All categories" title="All categories" className={cn(FOCUS_RING, "shrink-0 w-12 min-h-16 flex flex-col items-center justify-center gap-1 rounded-2xl border border-slate-200 bg-white text-[#1e3a5f] hover:bg-slate-50")}><LayoutGrid size={20}/><span className="text-[9px] font-semibold">Categories</span></button>
          </div>
        </div>
      </div>
      <Dialog open={showCategories} onOpenChange={setShowCategories}><DialogContent aria-describedby={undefined} className="bg-white rounded-[28px] max-w-lg max-h-[85dvh] overflow-y-auto p-6"><DialogTitle className="text-2xl font-bold tracking-tight text-[#1e3a5f]">Choose a category</DialogTitle><div className="grid grid-cols-1 gap-2 pt-3">{["All", ...subCats].map(name => { const photo = categoryImages[name]; return <button key={name} type="button" aria-pressed={subcategory === name} onClick={() => { setSubcategory(name); setShowCategories(false); }} className={cn("min-h-16 flex items-center gap-3 p-3 rounded-2xl border text-left text-sm font-semibold text-[#1e3a5f]", subcategory === name ? "border-[#f0a500] bg-[#fff8e6]" : "border-slate-200 bg-white hover:bg-slate-50")}>{photo ? <img src={photo} alt="" className="w-10 h-10 rounded-full object-cover shrink-0"/> : <span className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center shrink-0"><ShoppingBag size={18}/></span>}<span className="min-w-0 break-words">{name}</span></button>; })}</div></DialogContent></Dialog>



      {/* ── Feed ──────────────────────────────────────────────────────── */}
      <div className="container mx-auto px-5 py-6 sm:py-8">
        {recentStores.length > 0 && (
          <section className="mb-5">
            {storeType === "food" ? (
              <button type="button" onClick={() => setRecentOpen(open => !open)} aria-expanded={recentOpen}
                className={cn(FOCUS_RING, "w-full min-h-11 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 text-left text-sm font-semibold text-[#1e3a5f] hover:bg-slate-50")}>
                <span>Recently viewed ({recentStores.length})</span>
                <ChevronDown size={17} className={cn("transition-transform", recentOpen && "rotate-180")} aria-hidden />
              </button>
            ) : <h2 className={cn(TYPE.title3, "text-slate-900 mb-2")}>Recently viewed</h2>}
            {(storeType !== "food" || recentOpen) && (
              <div className={storeType === "food" ? "mt-3" : undefined}><ScrollRow>
                {recentStores.map((s) => <StoreCard key={s.id} store={s} variant="rail" />)}
              </ScrollRow></div>
            )}
          </section>
        )}

        <div className="flex items-baseline justify-between gap-4 mb-5"><h2 className="text-[27px] sm:text-[32px] leading-tight font-bold tracking-[-1px] text-[#1e3a5f]">{subcategory !== "All" ? subcategory : search ? "Search results" : "Your next favourite"}</h2><span className="text-xs text-slate-500 shrink-0">{stores.length} places</span></div>
        <QueryState
          isLoading={isLoading}
          isError={isError}
          isEmpty={stores.length === 0}
          onRetry={refetch}
          skeleton={skeleton}
          errorTitle="Couldn't load shops"
          errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
          emptyIcon={<ShoppingBag size={28} />}
          emptyTitle={hasActiveFilter ? "No matching shops" : "No stores found"}
          emptyBody={
            hasActiveFilter
              ? "Nothing matches these filters right now. Try clearing them or widening your area."
              : storefront.emptyText
          }
          emptyAction={
            hasActiveFilter
              ? { label: "Clear filters", onClick: clearFilters }
              : user ? { label: hasStore ? "Manage My Shops" : "Register a Shop", href: "/business/order-store" } : undefined
          }
        >
          <>
            {openStores.length > 0 && renderStores(openStores, true)}

            {closedStores.length > 0 && (
              <>
                <div className="flex items-center gap-3 mt-7 mb-3">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className={cn(TYPE.overline, "text-slate-400")}>Closed now ({closedStores.length})</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
                {renderStores(closedStores)}
              </>
            )}
          </>
        </QueryState>

        {/* Cross-sell lives after the feed on phone and tablet, where it cannot
            delay the first restaurant. From `lg` there is room for it inline in
            the header row above, so this copy is hidden there. */}
        <Link
          href={storefront.otherHref}
          className={cn(
            RADII.md, FOCUS_RING,
            "lg:hidden mt-6 flex items-center gap-3 border px-4 py-3 transition-colors",
            storeType === "food"
              ? "border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
              : "border-orange-200 bg-orange-50 hover:bg-orange-100",
          )}
        >
          <Store size={22} aria-hidden />
          <span className="flex-1 min-w-0">
            <span className={cn(TYPE.bodyStrong, "block", storeType === "food" ? "text-emerald-800" : "text-orange-800")}>
              {storefront.otherTitle}
            </span>
            <span className={cn(TYPE.footnote, "block text-slate-500")}>{storefront.otherText}</span>
          </span>
          <span className={cn("font-bold", storeType === "food" ? "text-emerald-700" : "text-orange-700")} aria-hidden>→</span>
        </Link>
        <div className="mt-8 pt-5 border-t border-slate-100 flex justify-end">              {user && (
                <Link href="/business/order-store">
                  <Button size="sm" className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90">
                    {hasStore ? "Manage My Shops" : "+ Register Your Shop"}
                  </Button>
                </Link>
              )}</div>
      </div>


    </div>
  );
}

export default function OrderStores() {
  return <StorefrontStores storeType="food" />;
}

export function ShopLocalStores() {
  return <StorefrontStores storeType="retail" />;
}
